import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, User, MessageSquare } from 'lucide-react';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSent(true);
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setError('Failed to send. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please email us at sales@rickysbees.com.au');
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="contact-heading">
            Contact Us
          </h1>
          <p className="text-foreground/70 text-center mb-10">
            Got a question, feedback, or just want to say hello? We'd love to hear from you.
          </p>

          {sent ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16 p-8 rounded-2xl border border-border bg-muted/30" data-testid="contact-success">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <Send className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Message Sent!</h2>
              <p className="text-foreground/70">Thanks for reaching out. We'll get back to you as soon as possible.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 rounded-2xl border border-border bg-muted/30 space-y-5" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" />
                  <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your name" className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" data-testid="contact-name-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" />
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Subject</label>
                <input type="text" name="subject" value={form.subject} onChange={handleChange} placeholder="What's this about?" className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" data-testid="contact-subject-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Message *</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" />
                  <textarea name="message" value={form.message} onChange={handleChange} rows={5} placeholder="Tell us what's on your mind..." className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm resize-none" data-testid="contact-message-input" />
                </div>
              </div>
              {error && <p className="text-red-500 text-sm" data-testid="contact-error">{error}</p>}
              <button type="submit" disabled={sending} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold text-lg flex items-center justify-center space-x-2 disabled:opacity-50" data-testid="contact-submit-button">
                <Send className="w-5 h-5" />
                <span>{sending ? 'Sending...' : 'Send Message'}</span>
              </button>
            </form>
          )}

          <div className="text-center mt-8">
            <p className="text-sm text-foreground/60">
              Or email us directly at <a href="mailto:sales@rickysbees.com.au" className="text-primary font-semibold hover:underline">sales@rickysbees.com.au</a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
